import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Link, Copy, Check, ExternalLink } from "lucide-react";
import { useCreateCheckoutSession, useCreateCheckoutForSale } from "@/hooks/useMercadoPago";
import { toast } from "sonner";

interface CreateCheckoutButtonProps {
  saleId?: string;
  items: Array<{
    product_id?: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    variation_id?: string;
    variation_description?: string;
    image_url?: string;
  }>;
  subtotal: number;
  discount?: number;
  disabled?: boolean;
}

export function CreateCheckoutButton({
  saleId,
  items,
  subtotal,
  discount,
  disabled,
}: CreateCheckoutButtonProps) {
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createSession = useCreateCheckoutSession();
  const createForSale = useCreateCheckoutForSale();

  const isPending = createSession.isPending || createForSale.isPending;

  const handleCreateCheckout = async () => {
    try {
      const formattedItems = items.map((item) => ({
        product_id: item.product_id,
        name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        variation_id: item.variation_id,
        variation_description: item.variation_description,
        image_url: item.image_url,
      }));

      let result;

      if (saleId) {
        result = await createForSale.mutateAsync({
          saleId,
          items: formattedItems,
          subtotal,
          discount,
        });
      } else {
        result = await createSession.mutateAsync({
          items: formattedItems,
          subtotal,
        });
      }

      const baseUrl = window.location.origin;
      const url = `${baseUrl}/checkout/${result.session.id}`;
      setCheckoutUrl(url);

      await navigator.clipboard.writeText(url);
      toast.success("Link gerado e copiado!");
    } catch {
      // Error toast already shown by useMutation onError
    }
  };

  const handleCopy = async () => {
    if (checkoutUrl) {
      await navigator.clipboard.writeText(checkoutUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (checkoutUrl) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input value={checkoutUrl} readOnly className="flex-1 text-xs sm:text-sm" />
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-4 w-4 text-primary" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={() => window.open(checkoutUrl, "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Link expira em 25 minutos
        </p>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled || items.length === 0 || isPending}
      onClick={handleCreateCheckout}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Link className="h-4 w-4 mr-2" />
      )}
      {isPending ? "Gerando..." : "Gerar Link de Checkout"}
    </Button>
  );
}
